import { Layout, Menu, Button, Typography, Space, Avatar } from 'antd';
import type { MenuProps } from 'antd';
import {
  BankOutlined,
  ContactsOutlined,
  ControlOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileTextOutlined,
  FundOutlined,
  LogoutOutlined,
  MessageOutlined,
  ProfileOutlined,
  SettingOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/shared/auth/auth-store';
import { useCurrentUser, ADMIN_ROLES, ACCOUNTANT_ROLES } from '@/shared/auth/role';
import { api } from '@/shared/lib/api';
import { LanguageSwitcher } from '@/shared/i18n/language-switcher';

const { Sider, Header, Content } = Layout;

export function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const currentUser = useCurrentUser();
  const role = currentUser?.role;
  const isAdmin = role ? ADMIN_ROLES.includes(role) : false;
  const isAccountant = role ? ACCOUNTANT_ROLES.includes(role) : false;

  // Logical grouping: a standalone dashboard, then Sales, Finance and Settings
  // sections. Each leaf carries a `show` flag; empty groups are dropped so a
  // role that can't see any item in a section never sees its header.
  type Leaf = { key: string; icon: JSX.Element; label: string; show: boolean };
  const sections: Array<{ label?: string; items: Leaf[] }> = [
    {
      items: [
        { key: '/owner', icon: <FundOutlined />, label: t('nav.owner'), show: isAdmin },
        { key: '/dashboard', icon: <DashboardOutlined />, label: t('nav.reports'), show: true },
      ],
    },
    {
      label: t('nav.sectionSales'),
      items: [
        { key: '/projects',  icon: <BankOutlined />,     label: t('nav.projects'),  show: true },
        { key: '/customers', icon: <TeamOutlined />,     label: t('nav.customers'), show: true },
        { key: '/contracts', icon: <FileTextOutlined />, label: t('nav.contracts'), show: true },
      ],
    },
    {
      label: t('nav.sectionFinance'),
      items: [
        { key: '/payments',       icon: <WalletOutlined />,   label: t('nav.payments'),       show: true },
        { key: '/kassa',          icon: <DollarOutlined />,   label: t('nav.kassa'),          show: isAccountant },
        { key: '/counterparties', icon: <ContactsOutlined />, label: t('nav.counterparties'), show: isAccountant },
      ],
    },
    {
      label: t('nav.settings'),
      items: [
        { key: '/settings',           icon: <ControlOutlined />, label: t('nav.generalSettings'),   show: isAdmin },
        { key: '/cash-categories',    icon: <TagsOutlined />,    label: t('nav.cashCategories'),    show: isAdmin },
        { key: '/document-templates', icon: <ProfileOutlined />, label: t('nav.documentTemplates'), show: isAdmin },
        { key: '/sms',                icon: <MessageOutlined />, label: t('nav.sms'),               show: isAdmin },
        { key: '/users',              icon: <SettingOutlined />, label: t('nav.users'),             show: isAdmin },
      ],
    },
  ];

  const leaves = sections.flatMap((s) => s.items).filter((i) => i.show);
  const menuItems: MenuProps['items'] = [];
  for (const s of sections) {
    const visible = s.items.filter((i) => i.show).map(({ show: _show, ...rest }) => rest);
    if (visible.length === 0) continue;
    if (s.label) menuItems.push({ type: 'group', label: s.label, children: visible });
    else menuItems.push(...visible);
  }

  // Longest-prefix match so e.g. /kassa/:id highlights Kassa correctly.
  const selectedKey =
    [...leaves]
      .sort((a, b) => b.key.length - a.key.length)
      .find((i) => location.pathname.startsWith(i.key))?.key ?? '/dashboard';

  async function handleLogout() {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    clear();
    navigate('/login', { replace: true });
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider theme="light" width={220} style={{ borderRight: '1px solid #f0f0f0' }}>
        <div style={{ padding: '20px 16px 12px', textAlign: 'center', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <img
            src="/pop-garden-logo.png"
            alt="Pop Garden"
            style={{ width: 150, maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
            {t('app.tagline')}
          </Typography.Text>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={(e) => navigate(e.key)}
          style={{ borderInlineEnd: 'none' }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: '1px solid #f0f0f0',
            height: 56,
          }}
        >
          <Space size={12}>
            <LanguageSwitcher />
            <span
              aria-hidden="true"
              style={{ width: 1, height: 20, background: '#e2e8f0' }}
            />
            <Avatar size={28} icon={<UserOutlined />} style={{ background: '#16a34a' }} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{user?.fullName}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{user?.role}</span>
            </div>
            <Button size="small" icon={<LogoutOutlined />} onClick={handleLogout}>
              {t('common.logout')}
            </Button>
          </Space>
        </Header>

        <Content style={{ padding: 24, background: '#f8fafc' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
